/* af1c-zoom-mobile.js — in-place pinch zoom on the product page.
 *
 * No modal, no backdrop. Two fingers on the shirt → the shirt image scales
 * up in place, anchored to the pinch midpoint. Release → smooth snap back
 * to 1×. Same aesthetic as desktop hover zoom, driven by touch.
 *
 * - Pinch-out (two fingers spreading) scales 1× → 4×
 * - Pinch midpoint anchors the zoom (like iOS Photos)
 * - Two-finger drag pans the zoomed image
 * - Release both fingers → 220ms ease back to 1× (no lingering zoom)
 * - Vertical scroll (one finger) still works normally
 * - Print-region gate: pinch only activates when both fingers start over
 *   the print graphic, so casual touches on the fabric don't trigger it
 *
 * Desktop entry point window.af1cOpenFullscreen is retained as a no-op
 * (desktop hover zoom in af1c-zoom.js is the desktop experience).
 */
(function () {
  'use strict';

  var MOBILE_MQ = window.matchMedia('(max-width: 900px)');
  var IS_MOBILE = MOBILE_MQ.matches;

  if (!IS_MOBILE) {
    // Desktop: expose a no-op so af1c-zoom.js's click handler doesn't crash.
    // (Desktop hover already provides zoom; click doesn't need to do anything
    // now that we've killed the fullscreen viewer.)
    window.af1cOpenFullscreen = function () {};
    return;
  }

  // Print region (kept in sync with af1c-zoom.js).
  var PRINT_REGION = { x1: 0.28, y1: 0.22, x2: 0.72, y2: 0.58 };

  var MAX_ZOOM = 4;
  var MIN_ZOOM = 1;
  var RELEASE_MS = 220;
  var EASE = 'cubic-bezier(0.22, 0.61, 0.36, 1)';

  function overPrint(hero, clientX, clientY) {
    var box = hero.getBoundingClientRect();
    var iw = hero.naturalWidth  || box.width;
    var ih = hero.naturalHeight || box.height;
    var imgAspect = (iw && ih) ? (iw / ih) : 1;
    var boxAspect = box.width / box.height;
    var rW, rH, oX, oY;
    if (imgAspect > boxAspect) {
      rW = box.width; rH = rW / imgAspect;
      oX = 0; oY = (box.height - rH) / 2;
    } else {
      rH = box.height; rW = rH * imgAspect;
      oY = 0; oX = (box.width - rW) / 2;
    }
    var ix = (clientX - box.left - oX) / rW;
    var iy = (clientY - box.top  - oY) / rH;
    return ix >= PRINT_REGION.x1 && ix <= PRINT_REGION.x2
        && iy >= PRINT_REGION.y1 && iy <= PRINT_REGION.y2;
  }

  function attachPinch(hero) {
    if (!hero || hero.__af1cPinchAttached) return;
    hero.__af1cPinchAttached = true;

    // Ensure the parent clips overflow so the scaled shirt doesn't spill
    // onto adjacent layout while zoomed.
    var parent = hero.parentElement;
    if (parent && getComputedStyle(parent).overflow === 'visible') {
      parent.style.overflow = 'hidden';
    }
    hero.style.willChange = 'transform';
    hero.style.transformOrigin = '50% 50%';

    // State
    var pinch = null;   // active pinch session
    var scale = 1, tx = 0, ty = 0;

    function apply(newScale, newTx, newTy, animate) {
      if (newScale < MIN_ZOOM) newScale = MIN_ZOOM;
      if (newScale > MAX_ZOOM) newScale = MAX_ZOOM;
      // Clamp pan so image edges don't drift too far past the container.
      var box = hero.getBoundingClientRect();
      var maxPanX = (newScale - 1) * box.width  / 2;
      var maxPanY = (newScale - 1) * box.height / 2;
      newTx = Math.max(-maxPanX, Math.min(maxPanX, newTx));
      newTy = Math.max(-maxPanY, Math.min(maxPanY, newTy));
      scale = newScale; tx = newTx; ty = newTy;
      hero.style.transition = animate ? ('transform ' + RELEASE_MS + 'ms ' + EASE) : '';
      hero.style.transform = 'translate(' + tx + 'px,' + ty + 'px) scale(' + scale + ')';
    }

    function reset(animate) {
      apply(1, 0, 0, animate);
    }

    // ---- Two-finger pinch (native touch events for reliability on iOS) ----
    // Attach in capture phase so we see the second-finger touchstart before
    // the parent swipe handler on <main> can decide it's a swipe.
    hero.addEventListener('touchstart', function (e) {
      if (e.touches.length === 2) {
        var a = e.touches[0], b = e.touches[1];
        // Gate: both fingers must start over the print region.
        if (!overPrint(hero, a.clientX, a.clientY) ||
            !overPrint(hero, b.clientX, b.clientY)) {
          return;
        }
        e.preventDefault();
        e.stopPropagation();
        var d0 = Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
        var mx = (a.clientX + b.clientX) / 2;
        var my = (a.clientY + b.clientY) / 2;
        // Convert midpoint to transform-origin percentages so pinch anchors
        // at the two-finger midpoint (like iOS Photos).
        var box = hero.getBoundingClientRect();
        var oxPct = ((mx - box.left) / box.width) * 100;
        var oyPct = ((my - box.top)  / box.height) * 100;
        hero.style.transformOrigin = oxPct.toFixed(2) + '% ' + oyPct.toFixed(2) + '%';
        pinch = {
          d0: d0, mx: mx, my: my,
          s0: scale, tx0: tx, ty0: ty,
        };
      }
    }, { passive: false, capture: true });

    hero.addEventListener('touchmove', function (e) {
      if (!pinch || e.touches.length < 2) return;
      e.preventDefault();
      e.stopPropagation();
      var a = e.touches[0], b = e.touches[1];
      var d = Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
      var factor = d / pinch.d0;
      var newScale = pinch.s0 * factor;
      // Track midpoint drift so two-finger drag also pans the zoomed image.
      var mx = (a.clientX + b.clientX) / 2;
      var my = (a.clientY + b.clientY) / 2;
      var newTx = pinch.tx0 + (mx - pinch.mx);
      var newTy = pinch.ty0 + (my - pinch.my);
      apply(newScale, newTx, newTy, false);
    }, { passive: false });

    function endPinch(e) {
      if (!pinch) return;
      // End when we drop below 2 fingers.
      if (e.touches && e.touches.length >= 2) return;
      pinch = null;
      // Snap back to 1× on release for a clean "peek then release" feel.
      // Reset origin to center so the ease-back looks correct.
      hero.style.transformOrigin = '50% 50%';
      // First move to where we currently are but with transformOrigin center,
      // then animate to identity. This avoids a jump when the origin resets.
      // Cheapest: just animate to identity — the visual jump is negligible.
      reset(true);
    }
    hero.addEventListener('touchend', endPinch, { passive: true, capture: true });
    hero.addEventListener('touchcancel', endPinch, { passive: true, capture: true });
  }

  window.af1cOpenFullscreen = function () {};

  function init() {
    var hero = document.getElementById('hero-img');
    if (hero) attachPinch(hero);
    var mo = new MutationObserver(function () {
      var h = document.getElementById('hero-img');
      if (h && !h.__af1cPinchAttached) attachPinch(h);
    });
    mo.observe(document.body, { subtree: true, childList: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
